// The Templates tab's table (List archetype). Props-pure. Authoring left this
// file with the drawer: Add and Edit are navigations to the
// /simulations/templates/* routes, so there is no save handler, no agent
// option list, and no editor surface here.
import { useMemo, type ReactNode } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { cronLabel } from "../../lib/cronLabel";
import { formatDateTime } from "../../lib/format";
import { shortResourceId } from "../../lib/shellNavigation";
import type { ListQueryResult } from "../../lib/useListQuery";
import { Badge, Button, EmptyState, Inline, Text } from "../../ui";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import {
  TemplateRowActions,
  type TemplateRowActionHandlers,
} from "./TemplateRowActions";

type TemplateResponse = components["schemas"]["TemplateResponse"];

// Long free-text columns are width-bounded and ellipsized so the Actions cell
// stays on screen — a template named after its whole regression suite, or an
// agent named "… (US-East, Production)", used to push the row buttons past the
// right edge. The full value stays reachable as the native title.
const BOUNDED_COLUMNS = ["agent", "spec", "schedule"];

export interface TemplatesListViewProps {
  agentNamesById: ReadonlyMap<string, string>;
  deleteError: string | null;
  deleteTemplateId: string | null;
  expandedTemplateId: string | null;
  isDeletePending: boolean;
  pausedScheduleTemplateId: string | null;
  resumedScheduleTemplateId: string | null;
  onCancelDelete: () => void;
  onConfirmDelete: (template: TemplateResponse) => void;
  onOpenEdit: (templateId: string) => void;
  onPauseSchedule: (template: TemplateResponse) => void;
  onResumeSchedule: (template: TemplateResponse) => void;
  onRun: (templateId: string, agentId: string) => void;
  onStartDelete: (template: TemplateResponse) => void;
  onToggleLineage: (templateId: string) => void;
  onOpenInTranscripts: (batchId: string) => void;
  renderLineage: (template: TemplateResponse) => ReactNode;
  templates: ListQueryResult<TemplateResponse>;
}

function specString(spec: TemplateResponse["spec"], key: string): string {
  const value = spec?.[key];
  return typeof value === "string" ? value : "";
}

// The saved spec, read as a sentence. The table lists templates across agents,
// so it holds no agent-scoped option lists to resolve ids into names — and a
// row of `Version 01a0...6155 · Connection 01a0...bc6f` is UUID soup that
// answers nothing a reader came to ask. What is scannable without a lookup is
// the SHAPE of the run: how many personas, and whether it sweeps or samples.
// The four fixed axes are what the authoring route is for. A spec missing any
// of them cannot launch anything, and says so.
export function templateSpecSummary(template: TemplateResponse): string | null {
  const spec = template.spec;
  const personaIds = Array.isArray(spec?.persona_ids) ? spec.persona_ids : [];
  if (
    specString(spec, "agent_version_id").length === 0 ||
    specString(spec, "connection_id").length === 0 ||
    specString(spec, "scenario_set_id").length === 0 ||
    personaIds.length === 0
  ) {
    return null;
  }
  const sweep = spec?.sweep;
  const parts = [
    `${String(personaIds.length)} persona${personaIds.length === 1 ? "" : "s"}`,
  ];
  if (
    sweep !== null &&
    typeof sweep === "object" &&
    !Array.isArray(sweep) &&
    Object.keys(sweep).length > 0
  ) {
    parts.push("sweep");
  }
  if (spec?.sample !== null && spec?.sample !== undefined) {
    parts.push("sampled");
  }
  return parts.join(" · ");
}

// Cron is not a language users read. Prose when the expression is one of the
// ordinary shapes, the raw expression verbatim otherwise — never a guess.
function scheduleLabel(schedule: string | null): string {
  if (schedule === null || schedule.trim().length === 0) {
    return "No schedule";
  }
  return cronLabel(schedule) ?? schedule;
}

// The prose reads "Daily at 3:00 AM"; with no zone beside it that is the
// reader's local time, which is wrong for every template not scheduled in it.
// `cronLabel` is handed the expression alone and cannot know the zone, so the
// row supplies it. Short names are what a reader scans for ("UTC", "EDT",
// "GMT+9"); an id the runtime does not recognise falls back to itself rather
// than vanishing, because a wrong-looking zone beats a missing one.
function timezoneLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return (
      parts.find((part) => part.type === "timeZoneName")?.value ?? timezone
    );
  } catch {
    return timezone;
  }
}

function pauseReasonLabel(
  reason: TemplateResponse["schedule_pause_reason"],
): string {
  switch (reason) {
    case "auto_failures":
      return "automatic failures";
    case "auto_invalid":
      return "spec invalid";
    case "pre_activation":
      return "pre-activation";
    case "user":
      return "user";
    default:
      return "unknown";
  }
}

function lastRunErrorLabel(errorCode: string): string {
  switch (errorCode) {
    case "QUOTA_EXCEEDED":
      return "Insufficient credits";
    case "overlap_skipped":
      return "Skipped — previous run still active";
    case "VALIDATION_FAILED":
      return "Spec invalid";
    default:
      return "Run failed";
  }
}

function templateColumns(
  handlers: TemplateRowActionHandlers & {
    agentNamesById: ReadonlyMap<string, string>;
  },
): Array<ColumnDef<TemplateResponse>> {
  const agentName = (template: TemplateResponse) =>
    handlers.agentNamesById.get(template.agent_id) ??
    `Agent ${shortResourceId(template.agent_id)}`;
  return [
    {
      header: "Name",
      id: "name",
      accessorFn: (template) => template.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Template ID"
          name={row.original.display_name}
          strong
          truncate
        />
      ),
    },
    {
      header: "Agent",
      id: "agent",
      accessorFn: agentName,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.agent_id}
          idLabel="Agent ID"
          name={agentName(row.original)}
          truncate
        />
      ),
    },
    {
      header: "Spec",
      id: "spec",
      accessorFn: (template) => templateSpecSummary(template) ?? "Incomplete",
      cell: ({ row }) => {
        const summary = templateSpecSummary(row.original);
        return summary === null ? (
          <Badge tone="danger">Incomplete</Badge>
        ) : (
          <Text truncate variant="muted">
            {summary}
          </Text>
        );
      },
    },
    {
      header: "Schedule",
      id: "schedule",
      accessorFn: (template) => scheduleLabel(template.schedule),
      cell: ({ row }) => {
        const schedule = row.original.schedule;
        return schedule === null || schedule.trim().length === 0 ? (
          <Badge>No schedule</Badge>
        ) : (
          // The raw expression is always the title, so a reader who wants to
          // verify the cron never loses it to the prose. The zone and the
          // scheduled marker sit outside the prose, not appended to the
          // sentence: only the prose carries `truncate`, so a narrow column
          // eats the describable half first and never the two parts of this
          // cell that must not be lost.
          <Inline gap={2}>
            <Text title={schedule} truncate>
              {scheduleLabel(schedule)}
            </Text>
            <Text variant="muted">
              {timezoneLabel(row.original.schedule_timezone)}
            </Text>
            <Badge tone="neutral">Scheduled</Badge>
          </Inline>
        );
      },
    },
    {
      header: "Next run",
      id: "next-run",
      accessorFn: (template) => template.next_fire_at ?? "",
      cell: ({ row }) => {
        const template = row.original;
        if (
          template.schedule === null ||
          template.schedule.trim().length === 0
        ) {
          return <Text variant="muted">-</Text>;
        }
        if (template.schedule_paused_at !== null) {
          return (
            <Badge tone="neutral">
              {`Paused (${pauseReasonLabel(template.schedule_pause_reason)})`}
            </Badge>
          );
        }
        return <DateCell value={template.next_fire_at} />;
      },
    },
    {
      header: "Last run",
      id: "last-run",
      accessorFn: (template) => template.last_fire_at ?? "",
      cell: ({ row }) => {
        const template = row.original;
        const lastFireBatchId = template.last_fire_batch_id;
        if (lastFireBatchId !== null) {
          return (
            <Button
              size="sm"
              type="button"
              variant="secondary"
              onClick={(event) => {
                event.stopPropagation();
                handlers.onOpenInTranscripts(lastFireBatchId);
              }}
            >
              {formatDateTime(template.last_fire_at)}
            </Button>
          );
        }
        if (template.last_fire_error_code !== null) {
          return (
            <Badge tone="danger">
              {lastRunErrorLabel(template.last_fire_error_code)}
            </Badge>
          );
        }
        return <Text variant="muted">-</Text>;
      },
    },
    {
      header: "Updated",
      id: "updated",
      accessorFn: (template) => template.updated_at,
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <TemplateRowActions handlers={handlers} template={row.original} />
      ),
    },
  ];
}

export function TemplatesListView({
  agentNamesById,
  deleteError,
  deleteTemplateId,
  expandedTemplateId,
  isDeletePending,
  pausedScheduleTemplateId,
  resumedScheduleTemplateId,
  onCancelDelete,
  onConfirmDelete,
  onOpenEdit,
  onOpenInTranscripts,
  onPauseSchedule,
  onResumeSchedule,
  onRun,
  onStartDelete,
  onToggleLineage,
  renderLineage,
  templates,
}: TemplatesListViewProps) {
  const columns = useMemo(
    () =>
      templateColumns({
        agentNamesById,
        deleteError,
        deleteTemplateId,
        expandedTemplateId,
        isDeletePending,
        pausedScheduleTemplateId,
        resumedScheduleTemplateId,
        onCancelDelete,
        onConfirmDelete,
        onOpenEdit,
        onOpenInTranscripts,
        onPauseSchedule,
        onResumeSchedule,
        onRun,
        onStartDelete,
        onToggleLineage,
      }),
    [
      agentNamesById,
      deleteError,
      deleteTemplateId,
      expandedTemplateId,
      isDeletePending,
      pausedScheduleTemplateId,
      resumedScheduleTemplateId,
      onCancelDelete,
      onConfirmDelete,
      onOpenEdit,
      onOpenInTranscripts,
      onPauseSchedule,
      onResumeSchedule,
      onRun,
      onStartDelete,
      onToggleLineage,
    ],
  );
  return (
    <PaginatedTableSection
      ariaLabel="Templates"
      boundedColumns={BOUNDED_COLUMNS}
      className="marker-data-table--narrow-bounds"
      columns={columns}
      emptyState={<EmptyState title="No templates" />}
      enableSorting
      errorMessage={templates.errorMessage}
      getRowId={(template) => template.id}
      hasNextPage={templates.hasNextPage}
      isFetchingNextPage={templates.isFetchingNextPage}
      isPending={templates.isPending}
      items={templates.items}
      listStateKey={templates.listStateKey}
      onLoadMore={templates.fetchNextPage}
      onRowClick={(row) => {
        onOpenEdit(row.original.id);
      }}
      isRowExpanded={(row) => row.original.id === expandedTemplateId}
      renderExpandedRow={(row) => renderLineage(row.original)}
      skeletonLabel="Loading templates"
      virtual={
        expandedTemplateId === null
          ? { fill: true, interactiveRows: true }
          : undefined
      }
    />
  );
}
